import { useState } from 'react';
import { Translate, ArrowsClockwise, CircleNotch } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * TranslateButton — vertaalknop voor elk tekstveld.
 *
 * Props:
 *  - text: de brontekst om te vertalen
 *  - targetLang: doeltaal code (default "nl")
 *  - sourceLang: brontaal code (optioneel, auto-detect)
 *  - onTranslated(translatedText, targetLang): callback met vertaalde tekst
 *  - size: "sm" | "md" (default "sm")
 *  - label: optionele knoplatst (default toont icoon)
 *  - className: extra css classes
 */
export function TranslateButton({ text, targetLang = 'nl', sourceLang = '', onTranslated, size = 'sm', label, className = '' }) {
  const [loading, setLoading] = useState(false);

  const doTranslate = async (e) => {
    e.stopPropagation();
    if (!text?.trim() || loading) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/translate`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, target_lang: targetLang, source_lang: sourceLang }),
      });
      if (res.ok) {
        const data = await res.json();
        onTranslated?.(data.translated, data.target_lang);
      }
    } catch { /* ignore */ }
    setLoading(false);
  };

  const sizeClasses = size === 'md'
    ? 'px-3 py-1.5 text-xs gap-1.5'
    : 'px-2 py-1 text-[10px] gap-1';

  return (
    <button
      onClick={doTranslate}
      disabled={loading || !text?.trim()}
      data-testid="translate-btn"
      className={`inline-flex items-center font-medium rounded-sm transition-colors
        bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100 disabled:opacity-40
        ${sizeClasses} ${className}`}
      title={`Vertaal naar ${targetLang === 'nl' ? 'Nederlands' : targetLang}`}
    >
      {loading ? <CircleNotch size={size === 'md' ? 14 : 11} className="animate-spin" /> : <Translate size={size === 'md' ? 14 : 11} />}
      {label || (size === 'md' ? 'Vertalen' : 'NL')}
    </button>
  );
}

/**
 * TranslateBackButton — vertaal tekst TERUG naar de originele taal voor verzending.
 *
 * Props:
 *  - text: de Nederlandse tekst
 *  - targetLang: de taal van de ontvanger (bijv. "de", "fr")
 *  - onTranslated(translatedText, targetLang): callback
 */
export function TranslateBackButton({ text, targetLang, onTranslated, className = '' }) {
  const [loading, setLoading] = useState(false);

  const langNames = { nl: 'Nederlands', en: 'Engels', de: 'Duits', fr: 'Frans', es: 'Spaans', it: 'Italiaans', pl: 'Pools', pt: 'Portugees' };

  const doTranslate = async (e) => {
    e.stopPropagation();
    if (!text?.trim() || !targetLang || targetLang === 'nl' || loading) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/translate`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, target_lang: targetLang, source_lang: 'nl' }),
      });
      if (res.ok) {
        const data = await res.json();
        onTranslated?.(data.translated, data.target_lang);
      }
    } catch { /* ignore */ }
    setLoading(false);
  };

  if (!targetLang || targetLang === 'nl') return null;

  return (
    <button
      onClick={doTranslate}
      disabled={loading || !text?.trim()}
      data-testid="translate-back-btn"
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-sm transition-colors
        bg-orange-50 text-orange-700 border border-orange-200 hover:bg-orange-100 disabled:opacity-40 ${className}`}
      title={`Terugvertalen naar ${langNames[targetLang] || targetLang}`}
    >
      {loading ? <CircleNotch size={12} className="animate-spin" /> : <ArrowsClockwise size={12} />}
      Terugvertalen → {langNames[targetLang] || targetLang.toUpperCase()}
    </button>
  );
}
